const ACCOUNT_UID_PREFIX = "acct_";
const ACCOUNT_UID_PATTERN = /^acct_[A-Za-z0-9_-]{10}$/;
const ACCOUNT_HOST_LABEL_PATTERN = /^acct-((?:[a-z0-9]|-[01a-z])+?)$/;

export function accountHostLabel(accountUid: string): string {
  if (!ACCOUNT_UID_PATTERN.test(accountUid)) throw new Error("Invalid account UID");
  const suffix = accountUid.slice(ACCOUNT_UID_PREFIX.length);
  return `acct-${[...suffix].map((character) => {
    if (/[a-z0-9]/.test(character)) return character;
    if (character === "_") return "-0";
    if (character === "-") return "-1";
    if (/[A-Z]/.test(character)) return `-${character.toLowerCase()}`;
    throw new Error("Invalid account UID character");
  }).join("")}`;
}

export function accountUidFromHostLabel(label: string): string | undefined {
  const match = label.toLowerCase().match(ACCOUNT_HOST_LABEL_PATTERN);
  if (!match) return undefined;
  let suffix = "";
  for (let index = 0; index < match[1].length; index += 1) {
    const character = match[1][index];
    if (character !== "-") {
      suffix += character;
      continue;
    }
    const escape = match[1][index + 1];
    if (!escape) return undefined;
    index += 1;
    if (escape === "0") suffix += "_";
    else if (escape === "1") suffix += "-";
    else if (/[a-z]/.test(escape)) suffix += escape.toUpperCase();
    else return undefined;
  }
  const accountUid = `${ACCOUNT_UID_PREFIX}${suffix}`;
  return ACCOUNT_UID_PATTERN.test(accountUid) ? accountUid : undefined;
}

export function accountUidFromHostname(hostname = window.location.hostname): string | undefined {
  return accountUidFromHostLabel(hostname.split(".")[0] || "");
}

export function accountParentHostname(hostname: string): string {
  const labels = hostname.split(".");
  if (labels.length > 1 && accountUidFromHostLabel(labels[0] || "")) {
    return labels.slice(1).join(".");
  }
  return hostname
    .replace(/^admin\./, "")
    .replace(/^(.+)-admin(\..+)$/, "$1$2");
}

function accountParentOrigin(): string {
  const configuredOrigin = import.meta.env.VITE_ACCOUNT_PUBLIC_ORIGIN?.replace(/\/$/, "");
  if (configuredOrigin) return configuredOrigin;

  const { hostname, port, protocol } = window.location;
  return `${protocol}//${accountParentHostname(hostname)}${port ? `:${port}` : ""}`;
}

export function accountPublicUrl(accountUid: string, path = "/"): string {
  const url = new URL(accountParentOrigin());
  url.hostname = `${accountHostLabel(accountUid)}.${url.hostname}`;
  return new URL(path.startsWith("/") ? path : `/${path}`, `${url.origin}/`).toString();
}
